import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronLeftIcon,
  PencilSquareIcon,
  PlusIcon,
  StarIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import apiService from '../../../services/ApiService';
import EditorChrome, { type EditorChromeVariant } from '../../ui/EditorChrome';
import Button from '../../ui/Button';
import ConfirmDialog from '../../ui/ConfirmDialog';
import { useToast } from '../../../contexts/ToastContext';
import type { AlbumMediaItem, AlbumType, MediaAlbum } from '../../../types/buildExpansion';

/**
 * Owner album manager: create/rename/delete albums, attach existing gallery
 * media, set covers, reorder with up/down buttons, and add external videos
 * (YouTube and Vimeo only, validated server-side).
 *
 * Gallery images are passed in by the parent; this modal never fetches them.
 */

export interface GalleryImage {
  id: number;
  image_url: string;
  title?: string | null;
}

const ALBUM_TYPE_LABELS: Record<AlbumType, string> = {
  overview: 'Overview',
  exterior: 'Exterior',
  interior: 'Interior',
  engine_bay: 'Engine bay',
  suspension: 'Suspension',
  fabrication: 'Fabrication',
  installation: 'Installation',
  before_after: 'Before and after',
  event: 'Event',
  track: 'Track',
  dyno: 'Dyno',
  professional_shoot: 'Professional shoot',
  restoration: 'Restoration',
  other: 'Other',
};
const ALBUM_TYPE_KEYS = Object.keys(ALBUM_TYPE_LABELS) as AlbumType[];

interface AlbumFormState {
  title: string;
  album_type: AlbumType;
  description: string;
  is_public: boolean;
}

const emptyAlbumForm: AlbumFormState = {
  title: '',
  album_type: 'other',
  description: '',
  is_public: true,
};

export interface AlbumManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  vehicleId: number;
  /** The vehicle's gallery images, provided by the parent (never fetched here). */
  galleryImages: GalleryImage[];
  /** Called after any album or media mutation so the parent can refresh. */
  onChanged?: () => void;
  /** `panel` embeds the manager inside Manage build without a nested dialog. */
  variant?: EditorChromeVariant;
}

export const AlbumManagerModal: React.FC<AlbumManagerModalProps> = ({
  isOpen,
  onClose,
  vehicleId,
  galleryImages,
  onChanged,
  variant = 'modal',
}) => {
  const { showToast } = useToast();

  const [albums, setAlbums] = useState<MediaAlbum[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState(false);

  // Create / edit album form
  const [formVisible, setFormVisible] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState<MediaAlbum | null>(null);
  const [albumForm, setAlbumForm] = useState<AlbumFormState>(emptyAlbumForm);
  const [albumFormError, setAlbumFormError] = useState('');

  const [deleteTarget, setDeleteTarget] = useState<MediaAlbum | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Detail view for one album
  const [selectedAlbum, setSelectedAlbum] = useState<MediaAlbum | null>(null);
  const [albumMedia, setAlbumMedia] = useState<AlbumMediaItem[]>([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [selectedToAdd, setSelectedToAdd] = useState<Set<number>>(new Set());
  const [videoUrl, setVideoUrl] = useState('');
  const [videoTitle, setVideoTitle] = useState('');
  const [videoError, setVideoError] = useState('');

  const notifyChanged = () => onChanged?.();

  const loadAlbums = useCallback(async () => {
    setLoadError('');
    try {
      const { albums: fetched } = await apiService.getMediaAlbums(vehicleId);
      setAlbums(fetched);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load albums.');
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setSelectedAlbum(null);
    setFormVisible(false);
    setEditingAlbum(null);
    setAlbumForm(emptyAlbumForm);
    setAlbumFormError('');
    setVideoUrl('');
    setVideoTitle('');
    setVideoError('');
    void loadAlbums();
  }, [isOpen, loadAlbums]);

  const loadAlbumMedia = useCallback(
    async (albumId: number) => {
      setMediaLoading(true);
      try {
        const media = await apiService.getAlbumMedia(vehicleId, albumId);
        setAlbumMedia(media);
      } catch (err) {
        showToast(err instanceof Error ? err.message : 'Failed to load album media.', 'error');
      } finally {
        setMediaLoading(false);
      }
    },
    [vehicleId, showToast]
  );

  const openAlbum = (album: MediaAlbum) => {
    setSelectedAlbum(album);
    setSelectedToAdd(new Set());
    setVideoUrl('');
    setVideoTitle('');
    setVideoError('');
    void loadAlbumMedia(album.id);
  };

  const startCreate = () => {
    setEditingAlbum(null);
    setAlbumForm(emptyAlbumForm);
    setAlbumFormError('');
    setFormVisible(true);
  };

  const startEdit = (album: MediaAlbum) => {
    setEditingAlbum(album);
    setAlbumForm({
      title: album.title,
      album_type: album.album_type,
      description: album.description ?? '',
      is_public: album.is_public,
    });
    setAlbumFormError('');
    setFormVisible(true);
  };

  const submitAlbumForm = async (e: React.FormEvent) => {
    e.preventDefault();
    const title = albumForm.title.trim();
    if (title === '' || title.length > 120) {
      setAlbumFormError('Album title is required (max 120 characters).');
      return;
    }
    setBusy(true);
    setAlbumFormError('');
    try {
      if (editingAlbum) {
        await apiService.mediaAlbumAction(vehicleId, 'update', {
          album_id: editingAlbum.id,
          title,
          album_type: albumForm.album_type,
          description: albumForm.description.trim() || null,
          is_public: albumForm.is_public,
        });
        showToast('Album updated.', 'success');
      } else {
        await apiService.mediaAlbumAction(vehicleId, 'create', {
          title,
          album_type: albumForm.album_type,
          description: albumForm.description.trim() || undefined,
          is_public: albumForm.is_public,
        });
        showToast('Album added.', 'success');
      }
      setFormVisible(false);
      setEditingAlbum(null);
      await loadAlbums();
      notifyChanged();
    } catch (err) {
      setAlbumFormError(err instanceof Error ? err.message : 'Failed to save the album.');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiService.mediaAlbumAction(vehicleId, 'delete', { album_id: deleteTarget.id });
      showToast('Album deleted. Media files stay in your gallery.', 'success');
      setDeleteTarget(null);
      if (selectedAlbum?.id === deleteTarget.id) setSelectedAlbum(null);
      await loadAlbums();
      notifyChanged();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to delete the album.', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // ── Detail view actions ──────────────────────────────────────────────────

  const imagesNotInAlbum = useMemo(() => {
    const inAlbum = new Set(albumMedia.map((m) => m.id));
    return galleryImages.filter((img) => !inAlbum.has(img.id));
  }, [galleryImages, albumMedia]);

  const toggleSelectToAdd = (imageId: number) => {
    setSelectedToAdd((prev) => {
      const next = new Set(prev);
      if (next.has(imageId)) {
        next.delete(imageId);
      } else {
        next.add(imageId);
      }
      return next;
    });
  };

  const addSelectedMedia = async () => {
    if (!selectedAlbum || selectedToAdd.size === 0) return;
    setBusy(true);
    try {
      await apiService.mediaAlbumAction(vehicleId, 'add_media', {
        album_id: selectedAlbum.id,
        image_ids: Array.from(selectedToAdd),
      });
      showToast('Media added to album.', 'success');
      setSelectedToAdd(new Set());
      await loadAlbumMedia(selectedAlbum.id);
      await loadAlbums();
      notifyChanged();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to add media.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const removeMedia = async (imageId: number) => {
    if (!selectedAlbum) return;
    setBusy(true);
    try {
      await apiService.mediaAlbumAction(vehicleId, 'remove_media', {
        album_id: selectedAlbum.id,
        image_id: imageId,
      });
      showToast('Removed from album. The file stays in your gallery.', 'success');
      await loadAlbumMedia(selectedAlbum.id);
      await loadAlbums();
      notifyChanged();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to remove media.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const setCover = async (imageId: number) => {
    if (!selectedAlbum) return;
    setBusy(true);
    try {
      await apiService.mediaAlbumAction(vehicleId, 'set_cover', {
        album_id: selectedAlbum.id,
        image_id: imageId,
      });
      showToast('Cover updated.', 'success');
      setSelectedAlbum((prev) => (prev ? { ...prev, cover_image_id: imageId } : prev));
      await loadAlbums();
      notifyChanged();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to set the cover.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const moveMedia = async (index: number, direction: -1 | 1) => {
    if (!selectedAlbum) return;
    const target = index + direction;
    if (target < 0 || target >= albumMedia.length) return;
    const reordered = [...albumMedia];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setAlbumMedia(reordered);
    setBusy(true);
    try {
      await apiService.mediaAlbumAction(vehicleId, 'reorder', {
        album_id: selectedAlbum.id,
        image_ids: reordered.map((m) => m.id),
      });
      notifyChanged();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to reorder media.', 'error');
      await loadAlbumMedia(selectedAlbum.id);
    } finally {
      setBusy(false);
    }
  };

  const addVideo = async (e: React.FormEvent) => {
    e.preventDefault();
    const url = videoUrl.trim();
    if (url === '') {
      setVideoError('Enter a video URL.');
      return;
    }
    setBusy(true);
    setVideoError('');
    try {
      const result = await apiService.mediaAlbumAction(vehicleId, 'add_video', {
        url,
        title: videoTitle.trim() || undefined,
        album_id: selectedAlbum?.id,
      });
      // The video is created as a gallery item; attach it to the open album.
      const newId = Number(result.id ?? 0);
      if (selectedAlbum && newId > 0) {
        await apiService.mediaAlbumAction(vehicleId, 'add_media', {
          album_id: selectedAlbum.id,
          image_ids: [newId],
        });
        await loadAlbumMedia(selectedAlbum.id);
      }
      showToast('Video added.', 'success');
      setVideoUrl('');
      setVideoTitle('');
      await loadAlbums();
      notifyChanged();
    } catch (err) {
      setVideoError(err instanceof Error ? err.message : 'Failed to add the video.');
    } finally {
      setBusy(false);
    }
  };

  // ── Rendering ────────────────────────────────────────────────────────────

  const renderAlbumForm = () => (
    <form
      onSubmit={submitAlbumForm}
      className="rounded-lg border border-white/[0.06] bg-[#0c0c0c]/50 p-4 space-y-4"
      aria-label={editingAlbum ? 'Edit album' : 'Create album'}
    >
      <h4 className="text-sm font-semibold text-white">
        {editingAlbum ? 'Edit album' : 'New album'}
      </h4>
      {albumFormError && (
        <div
          className="p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-200 text-sm"
          role="alert"
        >
          {albumFormError}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="album-title" className="input-label">
            Title *
          </label>
          <input
            type="text"
            id="album-title"
            value={albumForm.title}
            onChange={(e) => setAlbumForm((prev) => ({ ...prev, title: e.target.value }))}
            maxLength={120}
            className="input min-h-[44px]"
            placeholder="e.g. Engine bay refresh"
            disabled={busy}
            required
          />
        </div>
        <div>
          <label htmlFor="album-type" className="input-label">
            Album type
          </label>
          <select
            id="album-type"
            value={albumForm.album_type}
            onChange={(e) =>
              setAlbumForm((prev) => ({ ...prev, album_type: e.target.value as AlbumType }))
            }
            className="input min-h-[44px]"
            disabled={busy}
          >
            {ALBUM_TYPE_KEYS.map((key) => (
              <option key={key} value={key}>
                {ALBUM_TYPE_LABELS[key]}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label htmlFor="album-description" className="input-label">
          Description
        </label>
        <textarea
          id="album-description"
          value={albumForm.description}
          onChange={(e) => setAlbumForm((prev) => ({ ...prev, description: e.target.value }))}
          maxLength={500}
          rows={2}
          className="input resize-none"
          placeholder="Optional context for this album"
          disabled={busy}
        />
      </div>
      <div className="flex items-center">
        <input
          type="checkbox"
          id="album-is-public"
          checked={albumForm.is_public}
          onChange={(e) => setAlbumForm((prev) => ({ ...prev, is_public: e.target.checked }))}
          className="checkbox-control"
          disabled={busy}
        />
        <label htmlFor="album-is-public" className="ml-2 text-sm text-gray-300 min-h-[44px] flex items-center">
          Visible on the public build page
        </label>
      </div>
      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            setFormVisible(false);
            setEditingAlbum(null);
          }}
          disabled={busy}
          className="min-h-[44px]"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          loading={busy}
          loadingLabel="Saving…"
          className="min-h-[44px]"
        >
          {editingAlbum ? 'Save album' : 'Create album'}
        </Button>
      </div>
    </form>
  );

  const renderListView = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-gray-400">
          Albums organize existing gallery media. Files are never duplicated or deleted.
        </p>
        {!formVisible && (
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={startCreate}
            disabled={busy}
            className="min-h-[44px] shrink-0"
          >
            <PlusIcon className="w-4 h-4" aria-hidden />
            New album
          </Button>
        )}
      </div>

      {formVisible && renderAlbumForm()}

      {loading && (
        <div className="flex items-center gap-3 text-sm text-gray-400" role="status">
          <span className="spinner-sm" aria-hidden />
          Loading albums…
        </div>
      )}

      {!loading && loadError && (
        <div
          className="p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-200 text-sm"
          role="alert"
        >
          {loadError}{' '}
          <button
            type="button"
            onClick={() => {
              setLoading(true);
              void loadAlbums();
            }}
            className="underline hover:text-white transition-colors cursor-pointer"
          >
            Try again
          </button>
        </div>
      )}

      {!loading && !loadError && albums.length === 0 && !formVisible && (
        <p className="text-sm text-gray-400">No albums yet. Create one to group your media.</p>
      )}

      <ul className="space-y-2 list-none">
        {albums.map((album) => (
          <li
            key={album.id}
            className="rounded-lg border border-white/[0.06] bg-[#0c0c0c]/50 p-3 flex flex-wrap items-center gap-3"
          >
            {album.cover_url ? (
              <img
                src={album.cover_url}
                alt=""
                className="w-14 h-14 rounded object-cover bg-[#141414] shrink-0"
              />
            ) : (
              <div
                className="w-14 h-14 rounded bg-[#141414] shrink-0 flex items-center justify-center text-gray-600 text-xs"
                aria-hidden
              >
                No cover
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-white truncate">{album.title}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {ALBUM_TYPE_LABELS[album.album_type] ?? album.album_type} · {album.item_count}{' '}
                {album.item_count === 1 ? 'item' : 'items'}
                {!album.is_public && ' · Private'}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => openAlbum(album)}
                disabled={busy}
                className="min-h-[44px]"
              >
                Manage media
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => startEdit(album)}
                disabled={busy}
                className="min-h-[44px]"
                aria-label={`Edit album ${album.title}`}
              >
                <PencilSquareIcon className="w-4 h-4" aria-hidden />
                Edit
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setDeleteTarget(album)}
                disabled={busy}
                className="min-h-[44px] text-red-300 hover:text-red-200"
                aria-label={`Delete album ${album.title}`}
              >
                <TrashIcon className="w-4 h-4" aria-hidden />
                Delete
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );

  const renderDetailView = () => {
    if (!selectedAlbum) return null;
    return (
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setSelectedAlbum(null)}
            disabled={busy}
            className="min-h-[44px]"
          >
            <ChevronLeftIcon className="w-4 h-4" aria-hidden />
            All albums
          </Button>
          <div className="min-w-0">
            <h4 className="text-sm font-semibold text-white truncate">{selectedAlbum.title}</h4>
            <p className="text-xs text-gray-500">
              {ALBUM_TYPE_LABELS[selectedAlbum.album_type] ?? selectedAlbum.album_type}
            </p>
          </div>
        </div>

        <section aria-label="Album media">
          <h5 className="text-meta font-medium mb-2">Media in this album</h5>
          {mediaLoading ? (
            <div className="flex items-center gap-3 text-sm text-gray-400" role="status">
              <span className="spinner-sm" aria-hidden />
              Loading media…
            </div>
          ) : albumMedia.length === 0 ? (
            <p className="text-sm text-gray-400">
              Nothing here yet. Add photos from your gallery below.
            </p>
          ) : (
            <ul className="space-y-2 list-none">
              {albumMedia.map((item, index) => {
                const isCover = selectedAlbum.cover_image_id === item.id;
                const label = item.title || (item.media_kind === 'video_external' ? 'External video' : `Image ${item.id}`);
                return (
                  <li
                    key={item.id}
                    className="rounded-lg border border-white/[0.06] bg-[#0c0c0c]/50 p-2.5 flex items-center gap-3"
                  >
                    <img
                      src={item.image_url}
                      alt={item.alt_text || label}
                      className="w-12 h-12 rounded object-cover bg-[#141414] shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-gray-200 truncate">{label}</p>
                      <p className="text-xs text-gray-500">
                        {item.media_kind === 'video_external'
                          ? `Video · ${item.video_provider ?? 'external'}`
                          : 'Image'}
                        {isCover && ' · Cover'}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => void moveMedia(index, -1)}
                        disabled={busy || index === 0}
                        className="btn-icon min-w-[44px] min-h-[44px] disabled:opacity-40 cursor-pointer"
                        aria-label={`Move ${label} up`}
                      >
                        <ArrowUpIcon className="w-4 h-4" aria-hidden />
                      </button>
                      <button
                        type="button"
                        onClick={() => void moveMedia(index, 1)}
                        disabled={busy || index === albumMedia.length - 1}
                        className="btn-icon min-w-[44px] min-h-[44px] disabled:opacity-40 cursor-pointer"
                        aria-label={`Move ${label} down`}
                      >
                        <ArrowDownIcon className="w-4 h-4" aria-hidden />
                      </button>
                      <button
                        type="button"
                        onClick={() => void setCover(item.id)}
                        disabled={busy || isCover}
                        className="btn-icon min-w-[44px] min-h-[44px] disabled:opacity-40 cursor-pointer"
                        aria-label={isCover ? `${label} is the cover` : `Set ${label} as cover`}
                        aria-pressed={isCover}
                      >
                        {isCover ? (
                          <StarIconSolid className="w-4 h-4 text-orange-400" aria-hidden />
                        ) : (
                          <StarIcon className="w-4 h-4" aria-hidden />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => void removeMedia(item.id)}
                        disabled={busy}
                        className="btn-icon min-w-[44px] min-h-[44px] text-red-300 hover:text-red-200 disabled:opacity-40 cursor-pointer"
                        aria-label={`Remove ${label} from album`}
                      >
                        <TrashIcon className="w-4 h-4" aria-hidden />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section aria-label="Add gallery photos to album">
          <h5 className="text-meta font-medium mb-2">Add from your gallery</h5>
          {imagesNotInAlbum.length === 0 ? (
            <p className="text-sm text-gray-400">
              All of your gallery media is already in this album.
            </p>
          ) : (
            <>
              <ul className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 list-none">
                {imagesNotInAlbum.map((img) => {
                  const selected = selectedToAdd.has(img.id);
                  return (
                    <li key={img.id}>
                      <button
                        type="button"
                        onClick={() => toggleSelectToAdd(img.id)}
                        disabled={busy}
                        aria-pressed={selected}
                        aria-label={`${selected ? 'Deselect' : 'Select'} ${img.title || `image ${img.id}`}`}
                        className={`relative block w-full aspect-square rounded-lg overflow-hidden border-2 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 ${
                          selected ? 'border-orange-500' : 'border-white/[0.06] hover:border-white/[0.14]'
                        }`}
                      >
                        <img
                          src={img.image_url}
                          alt={img.title || ''}
                          className="w-full h-full object-cover"
                        />
                        {selected && (
                          <span
                            className="absolute inset-0 bg-orange-500/25 flex items-center justify-center"
                            aria-hidden
                          >
                            <span className="rounded-full bg-orange-500 text-white text-xs font-semibold px-2 py-0.5">
                              Selected
                            </span>
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
              <div className="mt-3">
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => void addSelectedMedia()}
                  disabled={busy || selectedToAdd.size === 0}
                  loading={busy && selectedToAdd.size > 0}
                  loadingLabel="Adding…"
                  className="min-h-[44px]"
                >
                  Add {selectedToAdd.size > 0 ? `${selectedToAdd.size} selected` : 'selected'}
                </Button>
              </div>
            </>
          )}
        </section>

        <section aria-label="Add external video">
          <h5 className="text-meta font-medium mb-2">Add external video</h5>
          <form onSubmit={addVideo} className="space-y-3">
            <div>
              <label htmlFor="video-url" className="input-label">
                Video URL
              </label>
              <input
                type="url"
                id="video-url"
                value={videoUrl}
                onChange={(e) => {
                  setVideoUrl(e.target.value);
                  if (videoError) setVideoError('');
                }}
                className={`input min-h-[44px] ${videoError ? 'border-red-500' : ''}`}
                placeholder="https://www.youtube.com/watch?v=…"
                disabled={busy}
                aria-invalid={Boolean(videoError)}
                aria-describedby={videoError ? 'video-url-error' : 'video-url-help'}
              />
              {videoError ? (
                <p id="video-url-error" className="mt-1 text-sm text-red-400" role="alert">
                  {videoError}
                </p>
              ) : (
                <p id="video-url-help" className="mt-1 text-xs text-gray-500">
                  YouTube and Vimeo links only.
                </p>
              )}
            </div>
            <div>
              <label htmlFor="video-title" className="input-label">
                Video title
              </label>
              <input
                type="text"
                id="video-title"
                value={videoTitle}
                onChange={(e) => setVideoTitle(e.target.value)}
                maxLength={200}
                className="input min-h-[44px]"
                placeholder="Optional title"
                disabled={busy}
              />
            </div>
            <Button
              type="submit"
              variant="secondary"
              size="sm"
              loading={busy && videoUrl.trim() !== ''}
              loadingLabel="Adding video…"
              disabled={busy}
              className="min-h-[44px]"
            >
              <PlusIcon className="w-4 h-4" aria-hidden />
              Add video
            </Button>
          </form>
        </section>
      </div>
    );
  };

  return (
    <>
      <EditorChrome
        variant={variant}
        isOpen={isOpen}
        onClose={() => {
          if (!busy && !deleting) onClose();
        }}
        title="Albums"
        description="Group your gallery media into albums for the build page."
        size="xl"
        closeOnOverlayClick={!busy && !deleting}
        closeOnEscape={!busy && !deleting}
        showClose={variant === 'modal'}
      >
        {selectedAlbum ? renderDetailView() : renderListView()}
      </EditorChrome>

      <ConfirmDialog
        isOpen={deleteTarget !== null}
        title="Delete this album?"
        description={
          deleteTarget
            ? `"${deleteTarget.title}" will be removed. The media files are not deleted and stay in your gallery.`
            : undefined
        }
        confirmLabel="Delete album"
        destructive
        loading={deleting}
        loadingLabel="Deleting…"
        onConfirm={handleDelete}
        onCancel={() => {
          if (!deleting) setDeleteTarget(null);
        }}
        closeOnOverlayClick={!deleting}
      />
    </>
  );
};

export default AlbumManagerModal;
